// Este archivo se encarga de exponer las variables de entorno de Vite al objeto window
// para que puedan ser utilizadas en archivos que se compilan tanto para el cliente como para el servidor

declare global {
  interface Window {
    __VITE_API_URL__?: string;
    __VITE_MODE__?: string;
    __VITE_GOOGLE_MAPS_API_KEY__?: string;
    __ENV_DEBUG__?: Record<string, any>;
    __VITE_ENV_MODE__?: string;
    __VITE_ENV_API_URL__?: string;
    __VITE_ENV_GOOGLE_MAPS_API_KEY__?: string;
  }
}

export function injectEnvToWindow(): void {
  if (typeof window === 'undefined') return;

  try {
    // Valores predeterminados
    let mode = 'production';
    let apiUrl = 'https://www.propiedadeslatam.com';
    let googleMapsApiKey = '';

    // En el cliente, intentamos obtener las variables de entorno
    // Nota: Este bloque se ejecutará solo en el cliente y no causará errores en el servidor
    if (typeof window !== 'undefined') {
      // Intentar obtener las variables del objeto global window
      // que pueden haber sido definidas por Vite en el proceso de compilación
      const envMode = window.__VITE_ENV_MODE__;
      const envApiUrl = window.__VITE_ENV_API_URL__;
      const envGoogleMapsApiKey = window.__VITE_ENV_GOOGLE_MAPS_API_KEY__;

      if (envMode) mode = envMode;
      if (envApiUrl) apiUrl = envApiUrl;
      if (envGoogleMapsApiKey) googleMapsApiKey = envGoogleMapsApiKey;
      
      // Corregir la URL de la API si es necesario
      if (window.location.hostname !== 'localhost' && apiUrl.includes('localhost')) {
        console.warn(`[clientEnv] ⚠️ La URL de la API (${apiUrl}) apunta a localhost pero la aplicación se está ejecutando en ${window.location.hostname}`);
        apiUrl = 'https://www.propiedadeslatam.com';
      }
      
      if (window.location.hostname === 'localhost' && !apiUrl.includes('localhost')) {
        console.warn(`[clientEnv] ⚠️ La URL de la API (${apiUrl}) apunta a producción pero la aplicación se está ejecutando en localhost`);
        apiUrl = 'http://localhost:3000';
      }

      // Verificar si hay un puerto específico en uso por el servidor
      // Intentar detectar el puerto del servidor a través de una solicitud
      if (window.location.hostname === 'localhost' && apiUrl.includes('localhost')) {
        // Verificar si hay un puerto específico almacenado en localStorage
        const storedPort = localStorage.getItem('server_port');
        if (storedPort) {
          apiUrl = `http://localhost:${storedPort}`;
          console.log(`[clientEnv] Usando puerto del servidor almacenado: ${storedPort}`);
        } else {
          // Intentar detectar el puerto automáticamente
          fetch('/api/health')
            .then(response => {
              const serverPort = response.headers.get('X-Server-Port') || '3000';
              localStorage.setItem('server_port', serverPort);
              apiUrl = `http://localhost:${serverPort}`;
              console.log(`[clientEnv] Puerto del servidor detectado: ${serverPort}`);
              window.__VITE_API_URL__ = apiUrl;
            })
            .catch(err => {
              console.warn('[clientEnv] No se pudo detectar el puerto del servidor:', err);
            });
        }
      }
    }

    // Guardar las variables en el objeto window
    window.__VITE_MODE__ = mode;
    window.__VITE_API_URL__ = apiUrl;
    window.__VITE_GOOGLE_MAPS_API_KEY__ = googleMapsApiKey;

    // Información de depuración
    window.__ENV_DEBUG__ = {
      mode,
      apiUrl,
      googleMapsApiKey,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      host: window.location.host
    };

    console.log('[clientEnv] Variables de entorno inyectadas en window:', window.__ENV_DEBUG__);
  } catch (e) {
    console.warn('[clientEnv] Error al inyectar variables de entorno en window:', e);
    
    // En caso de error, establecer valores predeterminados
    window.__VITE_MODE__ = 'production';
    window.__VITE_API_URL__ = 'https://www.propiedadeslatam.com';
    window.__VITE_GOOGLE_MAPS_API_KEY__ = '';
    
    window.__ENV_DEBUG__ = {
      error: true,
      errorMessage: e instanceof Error ? e.message : String(e),
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      host: window.location.host
    };
  }
}

interface ClientEnv {
  mode: string;
  apiUrl: string | undefined;
  googleMapsApiKey: string | undefined;
}

export function getClientEnv(): ClientEnv {
  return {
    mode: import.meta.env.MODE || 'production',
    apiUrl: import.meta.env.VITE_API_URL,
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY
  };
}

// Validar variables de entorno requeridas
export function validateEnv() {
  const env = getClientEnv();
  const missingVars: string[] = [];

  if (env.mode === 'production') {
    if (!env.apiUrl) missingVars.push('VITE_API_URL');
    if (!env.googleMapsApiKey) missingVars.push('VITE_GOOGLE_MAPS_API_KEY');
  }

  if (missingVars.length > 0) {
    console.error('Error: Missing required environment variables:', missingVars.join(', '));
    throw new Error(`Missing required environment variables: ${missingVars.join(', ')}`);
  }
} 